import { Check, Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';

const PHASE_ORDER = [
  'planning',
  'research',
  'content',
  'media',
  'render',
  'commit',
  'snapshot',
  'validate',
] as const;

const PHASE_LABELS: Record<(typeof PHASE_ORDER)[number], string> = {
  planning: 'Planning',
  research: 'Research',
  content: 'Writing content',
  media: 'Generating images',
  render: 'Building pages',
  commit: 'Finalizing',
  snapshot: 'Snapshot',
  validate: 'Quality checks',
};

interface Props {
  status?: string;
  lastCompletedPhase?: string;
  renderMode?: 'full' | 'metadata_only';
}

export function BuildProgress({ status, lastCompletedPhase, renderMode }: Props) {
  const isActive = status === 'running' || status === 'queued';
  const isMetadataOnly = renderMode === 'metadata_only';

  if (isActive && isMetadataOnly) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3 text-[13px] text-blue-900">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600" />
        </span>
        <span>
          Updating canonical URL + metadata across pages, then publishing. Your
          site goes live in about 5 minutes.
        </span>
      </div>
    );
  }

  const phaseIdx = lastCompletedPhase
    ? PHASE_ORDER.indexOf(lastCompletedPhase as (typeof PHASE_ORDER)[number])
    : -1;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {PHASE_ORDER.map((phase, idx) => {
          const done = idx <= phaseIdx;
          const active = idx === phaseIdx + 1 && status === 'running';
          const pending = !done && !active;
          return (
            <div
              key={phase}
              className={cn(
                'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11.5px] font-medium tracking-tightish transition-colors',
                done && 'bg-emerald-50 text-emerald-700 border border-emerald-200',
                active && 'bg-blue-50 text-blue-700 border border-blue-200',
                pending && 'bg-zinc-50 text-zinc-400 border border-zinc-200',
              )}
            >
              {done ? (
                <Check className="h-3 w-3" />
              ) : active ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
              )}
              {PHASE_LABELS[phase]}
            </div>
          );
        })}
      </div>
    </div>
  );
}
